import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useRoutesContext } from '@fs/zion-router'
import { type UseMutateAsyncFunction, useMutation, type UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { type AxiosError, type AxiosResponse } from '@fs/zion-axios'
import { playlistsConfig } from '../../config'
import type { Playlist, PlaylistsByItem } from '../../api/rootstech/playlist/playlist.types'
import { createPlaylist as createPlaylistApi, followPlaylist as followPlaylistApi } from '../../api'
import notifySentry from '../../lib/helpers/sentryNotifier'

type PlaylistsByItemsCache = { data: PlaylistsByItem }

type CreatePlaylistParams = {
  name?: string
  description?: string
  initialItem?: string
  seriesId?: string // For following a series
}

type UsePlaylistCreation = Omit<
  UseMutationResult<AxiosResponse<Playlist>, AxiosError, CreatePlaylistParams>,
  'mutateAsync'
> & {
  createPlaylist: UseMutateAsyncFunction<AxiosResponse<Playlist>, AxiosError, CreatePlaylistParams>
}

export const usePlaylistCreation = (): UsePlaylistCreation => {
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const { history } = useRoutesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: createPlaylist, ...rest } = useMutation<
    AxiosResponse<Playlist>,
    AxiosError,
    CreatePlaylistParams
  >({
    mutationFn: async ({ seriesId, name, description, initialItem }) => {
      // If seriesId is provided, this is a follow operation
      if (seriesId) {
        return followPlaylistApi({ seriesId })
      }
      // Otherwise, it's a standard playlist creation
      if (!name) {
        throw new Error('name is required for playlist creation')
      }
      return createPlaylistApi({ data: { name, description, initialItem } })
    },
    onSuccess: async ({ data: createdPlaylist }, variables) => {
      await queryClient.refetchQueries({ queryKey: ['playlists'] })

      // Safety net in case the refetched list is briefly behind the backend: ensure the new playlist is
      // present (deduped) so it's always in the cache the playlists page reads.
      queryClient.setQueryData<AxiosResponse<{ results: Playlist[] }>>(['playlists'], (oldResponse) => {
        const existingResults = oldResponse?.data?.results ?? []
        if (existingResults.some((playlist) => playlist.id === createdPlaylist.id)) {
          return oldResponse
        }
        return {
          ...oldResponse,
          data: {
            ...oldResponse?.data,
            results: [...existingResults, createdPlaylist],
          },
        } as AxiosResponse<{ results: Playlist[] }>
      })

      // Update playlistsByItems cache if an initialItem was added
      const { initialItem } = variables
      if (initialItem) {
        queryClient.setQueriesData<PlaylistsByItemsCache>({ queryKey: ['playlistsByItems'] }, (old) => {
          if (!old?.data) return old
          const currentSession = old.data[initialItem] ?? { results: [] }
          return {
            ...old,
            data: {
              ...old.data,
              [initialItem]: { results: [...currentSession.results, createdPlaylist] },
            },
          }
        })
        // Seed the playlistItems cache so the new playlist shows its item immediately
        queryClient.setQueryData(['playlistItems', createdPlaylist.id], {
          data: [{ id: initialItem, addedAt: new Date().toISOString() }],
        })
      }

      const { name, id } = createdPlaylist
      const isFollowing = Boolean(variables.seriesId)

      showStatusOverlay({
        message: isFollowing
          ? t('playlists.alerts.now-following', 'Following { name }.', { name })
          : t('playlists.alerts.created-new', 'Created playlist { playlistName }.', { playlistName: name }),
        type: 'success',
        actionLabel: t('playlists.saved.nav-to-playlist', 'Go to Playlist'),
        // The list is already settled above before this toast is shown, so navigation is a plain push.
        actionCallback: () => history.push(`/user/playlists/${id}`),
      })
    },
    onError: (err: AxiosError) => {
      let message = t(
        'playlists.errors.generic.creation-error',
        "We're sorry, but there was an issue creating your new playlist. Please try again shortly or refresh your browser."
      )
      if (err.response?.status === 422) {
        message = t(
          'playlists.alerts.playlist-limit-reached',
          'You have reached the maximum limit of {max} playlists. Please delete any old or non-essential playlists to make room for new ones.',
          {
            max: playlistsConfig.playlistLimit,
          }
        )
      } else {
        notifySentry({ ...err.response, err })
      }

      showStatusOverlay({
        message,
        type: 'error',
        dismissable: true,
      })
    },
  })
  return { createPlaylist, ...rest }
}
